import * as z from 'zod';
import { SuretyBondTermPaymentStatus, dateTimeSchema, suretyBondTermPaymentStatusSchema, suretyBondTermAdjustmentTypeSchema, SuretyBondTermAdjustmentType } from '../../../../enums.mjs';
import '../../../../types/actor/actor.mjs';
import '../../../../types/address/address.mjs';
import '../../../../types/credit-report/company-credit-report.mjs';
import '../../../../types/credit-report/individual-credit-report.mjs';
import '../../../../types/currency/currency.mjs';
import '../../../../types/entityRelation/entity-relation.mjs';
import '../../../../types/financial-formula/adjustable-value.mjs';
import '../../../../types/financial-formula/adjustable-values-update.mjs';
import '../../../../types/financial-formula/adjustment.mjs';
import '../../../../types/financial-formula/formula.mjs';
import '@inrev/core';
import '../../../../types/is-encrypted.mjs';
import '../../../../types/naics-codes/naics-codes.mjs';
import { isoDateSchema, fileId, suretyBondTermId, suretyBondTermAdjustmentId, percentSchemaV2, suretyBondId } from '../../../../types/opaque.mjs';
import '../../../../types/surety/account/surety-account.types.mjs';
import '../../../../types/surety/application-extraction/application-extraction-output.mjs';
import '../../../../types/surety/application-extraction/application-extraction-webhook.mjs';
import '../../../../types/surety/bond/surety-bond.types.mjs';
import '../../../../types/surety/bond-form/issued/issued-surety-bond-form.types.mjs';
import '../../../../types/surety/bond-form/surety-bond-form.data.mjs';
import '../../../../types/surety/bond-request/bond-request-extraction-output.mjs';
import '../../../../types/surety/bond-request/bond-request-extraction-webhook.mjs';
import '../../../../types/surety/contract/contract-extraction-output.mjs';
import '../../../../types/surety/contract/contract-extraction-webhook.mjs';
import '../../../../types/surety/financial-statement/financial-period/balance-sheet-data.mjs';
import '../../../../types/surety/financial-statement/financial-period/financial-period-extraction-webhook.mjs';
import '../../../../types/surety/financial-statement/financial-period/income-statement-data.mjs';
import '../../../../types/surety/financial-statement/financial-period/income-statement-fields.mjs';
import '../../../../types/surety/financial-statement/financial-period/income-statement-with-adjustments.mjs';
import '../../../../types/surety/financial-statement/financial-period/line-item-group.mjs';
import '../../../../types/surety/financial-statement/financial-period/section-keys.mjs';
import '../../../../types/surety/financial-statement/financial-period/statement-metadata.mjs';
import '../../../../types/surety/financial-statement/financial-period/view-shapes.mjs';
import '../../../../types/surety/financial-statement/financial-statement-schemas.mjs';
import '../../../../types/surety/indemnity/indemnity-agreement.mjs';
import '../../../../types/surety/quote/surety-quote.types.mjs';
import '../../../../types/surety/wip-schedule/wip-schedule-data.mjs';
import '../../../../types/surety/wip-schedule/wip-schedule-extraction-webhook.mjs';
import '../../../../types/surety/work-in-progress-statement/surety-work-in-progress-statement.mjs';
import '../../../../types/user/user.types.mjs';
import { currencySchema } from '../../../../types/utils.mjs';

const adjustmentInvoiceFields = {
    invoiceNumber: z.string().nullish(),
    invoiceFileId: fileId.nullish(),
    invoiceDate: isoDateSchema.nullish(),
};
const paidAdjustmentFields = {
    paymentStatus: z.literal(SuretyBondTermPaymentStatus.Paid),
    paidAt: dateTimeSchema,
};
const openAdjustmentFields = {
    paymentStatus: z
        .enum([SuretyBondTermPaymentStatus.Unpaid, SuretyBondTermPaymentStatus.Waived])
        .optional(),
};
var Adjustment;
(function (Adjustment) {
    (function (Get) {
        (function (Response) {
            Response.schema = z.object({
                id: suretyBondTermAdjustmentId,
                suretyBondTermId: suretyBondTermId,
                invoiceNumber: z.string().nullish(),
                invoiceFileId: fileId.nullish(),
                invoiceDate: isoDateSchema.nullish(),
                type: suretyBondTermAdjustmentTypeSchema,
                amount: currencySchema,
                premiumAmount: currencySchema.nullish(),
                paymentStatus: suretyBondTermPaymentStatusSchema,
                effectiveDate: isoDateSchema,
                paidAt: dateTimeSchema.nullish(),
                createdAt: dateTimeSchema,
                updatedAt: dateTimeSchema,
            });
            Response.baseSchema = Response.schema.extend({
                type: z.literal(SuretyBondTermAdjustmentType.Base),
            });
            Response.nonBaseSchema = Response.schema.extend({
                type: z.literal(SuretyBondTermAdjustmentType.Adjustment),
            });
        })(Get.Response || (Get.Response = {}));
    })(Adjustment.Get || (Adjustment.Get = {}));
    (function (Create) {
        (function (Request) {
            const fields = {
                ...adjustmentInvoiceFields,
                amount: currencySchema,
                effectiveDate: isoDateSchema,
            };
            Request.schema = z.union([
                z.object({ ...fields, ...paidAdjustmentFields }).strict(),
                z.object({ ...fields, ...openAdjustmentFields }).strict(),
            ]);
        })(Create.Request || (Create.Request = {}));
    })(Adjustment.Create || (Adjustment.Create = {}));
    (function (Update) {
        (function (Request) {
            const fields = {
                ...adjustmentInvoiceFields,
                amount: currencySchema.optional(),
                effectiveDate: isoDateSchema.optional(),
            };
            Request.schema = z.union([
                z.object({ ...fields, ...paidAdjustmentFields }).strict(),
                z.object({ ...fields, ...openAdjustmentFields }).strict(),
            ]);
        })(Update.Request || (Update.Request = {}));
    })(Adjustment.Update || (Adjustment.Update = {}));
})(Adjustment || (Adjustment = {}));
var Get;
(function (Get) {
    (function (Response) {
        Response.schema = z.object({
            id: suretyBondTermId,
            suretyBondId: suretyBondId,
            effectiveDate: isoDateSchema,
            expirationDate: isoDateSchema.nullish(),
            rate: percentSchemaV2,
            premium: currencySchema,
            totalPremium: currencySchema,
            adjustmentsTotal: currencySchema,
            lineItemCount: z.number().int().positive(),
            unpaidCount: z.number().int().nonnegative(),
            paymentStatus: suretyBondTermPaymentStatusSchema,
            status: z.enum(["expired", "current", "future"]),
            bondAmount: currencySchema,
            adjustments: z
                .tuple([Adjustment.Get.Response.baseSchema])
                .rest(Adjustment.Get.Response.nonBaseSchema),
            createdAt: dateTimeSchema,
            updatedAt: dateTimeSchema,
        });
    })(Get.Response || (Get.Response = {}));
})(Get || (Get = {}));
var Create;
(function (Create) {
    (function (Request) {
        Request.schema = z.object({
            rate: percentSchemaV2,
            premium: currencySchema,
            bondAmount: currencySchema,
            invoiceNumber: z.string().nullish(),
            invoiceFileId: fileId.nullish(),
            invoiceDate: isoDateSchema.nullish(),
        });
    })(Create.Request || (Create.Request = {}));
})(Create || (Create = {}));
var Update;
(function (Update) {
    (function (Request) {
        Request.schema = z.object({
            effectiveDate: isoDateSchema.optional(),
            expirationDate: isoDateSchema.nullish(),
            rate: percentSchemaV2.optional(),
            premium: currencySchema.optional(),
            bondAmount: currencySchema.optional(),
        });
    })(Update.Request || (Update.Request = {}));
})(Update || (Update = {}));

export { Adjustment, Create, Get, Update };
